'use client';

import { useState, useEffect } from 'react';
import { Search, MapPin } from 'lucide-react';

interface GemeindeDaten {
  plzToGemeinde: Record<string, string[]>;
  gemeindeToPlz: Record<string, string[]>;
  gemeindeToName: Record<string, string>;
  gemeindeToWappen: Record<string, string>;
}

interface GemeindeAuswahlProps {
  onSelectGemeinde: (gemeinde: string) => void;
}

interface Gemeinde {
  nr: string;
  name: string;
  plz: string;
}

import { GemeindeQuickSearch } from './GemeindeQuickSearch';
import { gemeindeListe } from '@/data/gemeindeListe';

// Named export for better tree-shaking and explicit imports
export function GemeindeAuswahl({ onSelectGemeinde }: GemeindeAuswahlProps) {
  const [plz, setPlz] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [gemeindeDaten, setGemeindeDaten] = useState<GemeindeDaten | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Use the modern QuickSearch component
  return (
    <div className="py-6">
      <GemeindeQuickSearch gemeinden={gemeindeListe} onSelect={onSelectGemeinde} />
    </div>
  );

  // Load municipality data on component mount
  useEffect(() => {
    const loadGemeindeDaten = async () => {
      try {
        const url = '/data/gemeinde-data.json';
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`Daten konnten nicht geladen werden (${response.status})`);
        }
        
        const data = await response.json();
        
        // Debug: Check if wappenLink data exists
        const gemeinden = data.gemeinden || [];
        const gemeindenWithWappen = gemeinden.filter((g: any) => g.wappenLink);
        console.log(`[GemeindeAuswahl] Found ${gemeindenWithWappen.length} gemeinden with wappen out of ${gemeinden.length} total`);
        
        // Transform to old format for compatibility
        const transformedData = {
          plzToGemeinde: (Object.entries(data.plzToGemeinde || {}) as [string, { gemeindeNummer: string }][])
            .reduce<Record<string, string[]>>((acc, [plz, info]) => {
              acc[plz] = [info.gemeindeNummer];
              return acc;
            }, {}),
          gemeindeToPlz: (data.gemeinden as Array<{ gemeindeNummer: string; plzList: string[] }>).reduce<Record<string, string[]>>((acc, g) => {
            acc[g.gemeindeNummer] = g.plzList;
            return acc;
          }, {}),
          gemeindeToName: (data.gemeinden as Array<{ gemeindeNummer: string; name: string }>).reduce<Record<string, string>>((acc, g) => {
            acc[g.gemeindeNummer] = g.name;
            return acc;
          }, {}),
          gemeindeToWappen: (data.gemeinden as Array<{ gemeindeNummer: string; wappenLink?: string }>).reduce<Record<string, string>>((acc, g) => {
            if (g.wappenLink) {
              acc[g.gemeindeNummer] = g.wappenLink;
            }
            return acc;
          }, {}),
        };
        
        console.log(`[GemeindeAuswahl] Wappen mapping created with ${Object.keys(transformedData.gemeindeToWappen).length} entries`);
        
        setGemeindeDaten(transformedData);
      } catch (error) {
        const errorMessage = error instanceof Error 
          ? error.message 
          : 'Ein unbekannter Fehler ist aufgetreten';
        setError(`Gemeindedaten konnten nicht geladen werden: ${errorMessage}. Bitte versuchen Sie es später erneut.`);
      } finally {
        setIsLoading(false);
      }
    };

    loadGemeindeDaten();
  }, []);

  // Filtere Gemeinden basierend auf Suchbegriff oder PLZ
  const getFilteredGemeinden = (): Gemeinde[] => {
    if (!gemeindeDaten) return [];
    
    // Suche nach PLZ
    if (plz.trim() && plz.trim().length >= 3) {
      const gemeindeNrs = gemeindeDaten.plzToGemeinde[plz] || [];
      return gemeindeNrs.map(nr => ({
        nr,
        name: gemeindeDaten.gemeindeToName[nr] || 'Unbekannte Gemeinde',
        plz: gemeindeDaten.gemeindeToPlz[nr]?.[0] || ''
      }));
    }
    
    // Suche nach Gemeindename
    if (searchTerm.trim()) {
      return Object.entries(gemeindeDaten.gemeindeToName)
        .filter(([, name]) => 
          name.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map(([nr, name]) => ({
          nr,
          name,
          plz: gemeindeDaten.gemeindeToPlz[nr]?.[0] || ''
        }));
    }
    
    return [];
  };

  const filteredGemeinden = getFilteredGemeinden();
  const hasSearchResults = searchTerm.trim().length > 0 || plz.trim().length > 0;
  const showNoResults = hasSearchResults && filteredGemeinden.length === 0 && !isLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Beliebte Gemeinden in Tirol
  const beliebteGemeinden: Gemeinde[] = [
    { nr: '70101', name: 'Innsbruck', plz: '6020' },
    { nr: '70201', name: 'Telfs', plz: '6410' },
    { nr: '70316', name: 'Kufstein', plz: '6330' },
    { nr: '70401', name: 'Kitzbühel', plz: '6370' },
    { nr: '70716', name: 'Lienz', plz: '9900' },
    { nr: '70203', name: 'Imst', plz: '6460' },
  ];

  return (
    <div className="text-center">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 max-w-2xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Finden Sie Ihre Gemeinde
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Geben Sie den Namen Ihrer Gemeinde oder die Postleitzahl ein
          </p>

          {/* PLZ Suche */}
          <div className="mb-6">
            <label htmlFor="plz" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
              Postleitzahl eingeben:
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MapPin className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="plz"
                value={plz}
                onChange={(e) => {
                  setPlz(e.target.value.replace(/[^0-9]/g, ''));
                  setSearchTerm('');
                }}
                className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-12 py-3 sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                placeholder="z.B. 6020"
                maxLength={4}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">
                  {plz.length}/4
                </span>
              </div>
            </div>
          </div>

          {/* Gemeindename Suche */}
          <div className="relative mb-6">
            <label htmlFor="gemeinde" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
              Oder Gemeindename eingeben:
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="gemeinde"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (e.target.value) setPlz('');
                }}
                className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-12 py-3 sm:text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md"
                placeholder="Gemeindename eingeben..."
              />
              {searchTerm && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Suche zurücksetzen</span>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Suchergebnisse */}
          {filteredGemeinden.length > 0 && (
            <div className="mt-2 mb-6 bg-white dark:bg-gray-700 rounded-lg shadow-lg overflow-hidden">
              {filteredGemeinden.map((gemeinde) => (
                <button
                  key={gemeinde.nr}
                  className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border-b border-gray-100 dark:border-gray-600 last:border-0"
                  onClick={() => onSelectGemeinde(gemeinde.nr)}
                >
                  <div className="font-medium text-gray-900 dark:text-white">{gemeinde.name}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">PLZ: {gemeinde.plz}</div>
                </button>
              ))}
            </div>
          )}

          {showNoResults && (
            <div className="text-gray-600 dark:text-gray-300 mb-6">
              Keine Ergebnisse gefunden.
            </div>
          )}
        </div>

        <button
          onClick={() => {
            if (plz) {
              // Hier könnten wir die PLZ in einen Gemeindenamen umwandeln
              onSelectGemeinde(`Gemeinde mit PLZ ${plz}`);
            } else if (searchTerm && filteredGemeinden.length > 0) {
              onSelectGemeinde(filteredGemeinden[0].nr);
            }
          }}
          disabled={!plz && (!searchTerm || filteredGemeinden.length === 0)}
          className={`w-full py-3 px-6 rounded-lg font-medium text-white transition-colors ${
            plz || (searchTerm && filteredGemeinden.length > 0)
              ? 'bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 shadow-lg'
              : 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
          }`}
        >
          Potenzial berechnen
        </button>

        {/* Beliebte Gemeinden */}
        <div className="mt-8">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3 text-left">
            Beliebte Gemeinden in Tirol:
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {beliebteGemeinden.map((gemeinde) => (
              <button
                key={gemeinde.nr}
                onClick={() => onSelectGemeinde(gemeinde.nr)}
                className="px-4 py-3 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg text-left transition-colors"
              >
                <div className="font-medium text-gray-900 dark:text-white">{gemeinde.name}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  PLZ: {gemeinde.plz}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
